import React from "react";
import { Container, Grid, Typography } from "@mui/material";
import {
  FaHandshake,
  FaUserTie,
  FaStar,
  FaRocket,
  FaCheckCircle,
  FaUsers,
} from "react-icons/fa";
import { motion } from "framer-motion";
import UniformCard from "../../components/UniformCard/UniformCard";

const values = [
  {
    title: "Integrity & Trust",
    description:
      "We uphold the highest ethical standards and foster long-term, trusted relationships.",
    icon: <FaHandshake size={45} />,
  },
  {
    title: "Client-Centricity",
    description:
      "Our clients’ success is our success—we listen, adapt, and tailor solutions to their unique needs.",
    icon: <FaUserTie size={45} />,
  },
  {
    title: "Excellence",
    description:
      "We hold ourselves to the highest standards, delivering exceptional quality and measurable impact.",
    icon: <FaStar size={45} />,
  },
  {
    title: "Entrepreneurial Spirit",
    description:
      "We embrace initiative, agility, and forward-thinking to drive growth and uncover opportunities.",
    icon: <FaRocket size={45} />,
  },
  {
    title: "Accountability",
    description:
      "We take ownership of our commitments and deliver on our promises, every time.",
    icon: <FaCheckCircle size={45} />,
  },
  {
    title: "Collaboration",
    description:
      "We believe in the power of teamwork—within our firm and alongside our clients.",
    icon: <FaUsers size={45} />,
  },
];

const OurValues = ({ showTitle = true, theme = "dark" }) => {
  if (theme === "light") {
    return (
      <section style={{ padding: "60px 0", background: "#f8fbff" }}>
        <Container>
          {showTitle && (
            <div style={{ textAlign: "center", marginBottom: "50px" }}>
              <Typography
                variant="overline"
                style={{
                  color: "var(--primary-color)",
                  fontWeight: 800,
                  letterSpacing: 2,
                  display: "block",
                  marginBottom: "10px",
                }}
              >
                Our Core Values
              </Typography>
              <Typography
                variant="h2"
                style={{
                  fontWeight: 800,
                  color: "var(--primary-color)",
                  fontFamily: "'Roboto Slab', serif",
                  fontSize: "2.5rem",
                }}
              >
                Built on Trust & Excellence
              </Typography>
              <div
                style={{
                  width: "60px",
                  height: "4px",
                  background: "var(--primary-color)",
                  margin: "15px auto",
                  borderRadius: "2px",
                }}
              />
            </div>
          )}
          <Grid container spacing={4}>
            {values.map((v, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <div
                  style={{
                    padding: "30px",
                    background: "#fff",
                    borderRadius: "20px",
                    height: "100%",
                    textAlign: "center",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                    border: "1px solid rgba(1, 30, 99, 0.05)",
                    transition: "transform 0.3s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-10px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0)")
                  }
                >
                  <div
                    style={{
                      color: "var(--primary-color)",
                      marginBottom: "20px",
                    }}
                  >
                    {v.icon}
                  </div>
                  <Typography
                    variant="h5"
                    style={{
                      fontWeight: 700,
                      marginBottom: "15px",
                      color: "var(--primary-color)",
                    }}
                  >
                    {v.title}
                  </Typography>
                  <Typography style={{ color: "#666", lineHeight: 1.6 }}>
                    {v.description}
                  </Typography>
                </div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>
    );
  }

  return (
    <section
      className="about-section section-padding"
      style={{
        position: "relative",
        background: "#011e63", // Deep Corporate Blue
        overflow: "hidden",
        padding: "40px 0",
      }}
    >
      {/* Dynamic Animated Background - Optimized with CSS animations */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          overflow: "hidden",
          willChange: "transform",
          transform: "translateZ(0)",
        }}
      >
        {/* Animated Mesh Blobs - Using CSS animations for better performance */}
        <div
          className="animated-blob-1"
          style={{
            position: "absolute",
            top: "-15%",
            left: "-10%",
            width: "60%",
            height: "60%",
            background:
              "radial-gradient(circle, rgba(46, 92, 226, 0.4) 0%, transparent 70%)",
            filter: "blur(60px)",
            willChange: "transform",
            animation: "blobFloat1 20s ease-in-out infinite",
          }}
        />
        <div
          className="animated-blob-2"
          style={{
            position: "absolute",
            bottom: "-15%",
            right: "-5%",
            width: "55%",
            height: "55%",
            background:
              "radial-gradient(circle, rgba(0, 255, 230, 0.15) 0%, transparent 70%)",
            filter: "blur(70px)",
            willChange: "transform",
            animation: "blobFloat2 25s ease-in-out infinite",
          }}
        />
        <div
          className="animated-blob-3"
          style={{
            position: "absolute",
            top: "20%",
            left: "20%",
            width: "100%",
            height: "100%",
            backgroundImage:
              "conic-gradient(from 0deg at 50% 50%, transparent, rgba(46, 92, 226, 0.1), transparent)",
            filter: "blur(30px)",
            willChange: "transform, opacity",
            animation: "blobRotate 40s linear infinite",
          }}
        />

        {/* HUD-like Grid Overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
            `,
            backgroundSize: "60px 60px",
            opacity: 0.3,
            maskImage:
              "radial-gradient(circle at center, black 40%, transparent 90%)",
          }}
        />

        {/* Floating Particles/Dots */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(rgba(255, 255, 255, 0.2) 1px, transparent 1px)",
            backgroundSize: "30px 30px",
            opacity: 0.2,
          }}
        />
      </div>

      {/* CSS Keyframe Animations */}
      <style>{`
        @keyframes blobFloat1 {
          0%, 100% { transform: scale(1) translate(0, 0); }
          50% { transform: scale(1.2) translate(50px, -30px); }
        }
        @keyframes blobFloat2 {
          0%, 100% { transform: scale(1) translate(0, 0); }
          50% { transform: scale(1.1) translate(-40px, 40px); }
        }
        @keyframes blobRotate {
          0% { transform: rotate(0deg); opacity: 0.3; }
          50% { opacity: 0.5; }
          100% { transform: rotate(360deg); opacity: 0.3; }
        }
      `}</style>

      <div
        className="container primary-color mb-5"
        style={{ position: "relative", zIndex: 1 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {showTitle && (
            <div className="section-title-s3 mb-4">
              <h2
                className="text-uppercase fw-bold text-white"
                style={{
                  fontSize: "3rem",
                  letterSpacing: "4px",
                  textShadow: "0 10px 30px rgba(0,0,0,0.5)",
                  background: "linear-gradient(to bottom, #fff, #a5b4fc)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Our Values
              </h2>
            </div>
          )}
          <h4
            className="fw-light mb-3 text-light"
            style={{
              maxWidth: "800px",
              margin: "0 auto",
              lineHeight: 1.8,
              fontSize: "1.2rem",
              opacity: 0.85,
              textAlign: "center",
            }}
          >
            Our core values drive every decision, ensuring integrity,
            excellence, and lasting impact for our clients.
          </h4>
        </motion.div>
      </div>

      <div className="container" style={{ position: "relative", zIndex: 1 }}>
        <div className="row g-4">
          {values.map((value, index) => (
            <motion.div
              key={index}
              className="col-md-6 col-lg-4"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <UniformCard
                icon={value.icon}
                title={value.title}
                description={value.description}
                sx={{
                  height: "100%",
                  background: "rgba(255, 255, 255, 0.03)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "20px",
                  color: "#fff",
                  padding: "40px 30px",
                  transition: "all 0.5s cubic-bezier(0.23, 1, 0.32, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(45deg, transparent, rgba(0, 255, 230, 0.05), transparent)",
                    transform: "translateX(-100%)",
                    transition: "transform 0.6s",
                  },
                  "& .icon-wrapper": {
                    color: "var(--turquoise-color)",
                    marginBottom: "25px",
                    transition:
                      "transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  },
                  "& h5": {
                    color: "#fff !important",
                    fontSize: "1.5rem",
                    fontWeight: "600",
                    marginBottom: "15px",
                  },
                  "& p": {
                    color: "#fff !important",
                    fontSize: "1rem",
                    lineHeight: "1.6",
                    textAlign: "center",
                  },
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.07)",
                    transform: "translateY(-15px) scale(1.02)",
                    boxShadow:
                      "0 25px 50px rgba(0,0,0,0.4), 0 0 20px rgba(0, 255, 230, 0.1)",
                    borderColor: "rgba(0, 255, 230, 0.4)",
                    "&::before": {
                      transform: "translateX(100%)",
                    },
                    "& .icon-wrapper": {
                      transform: "scale(1.2) rotate(10deg)",
                    },
                  },
                }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurValues;
